import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { tenantGuard } from './core/guards/tenant.guard';
import { whitelabelGuard } from './core/guards/whitelabel.guard';
import { AppShellComponent } from './layout/app-shell/app-shell.component';
import { adminRoutes } from './admin/admin.routes';
import { crmRoutes } from './features/crm/crm.routes';

const tenantFeatureRoutes: Routes = [
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
    data: { breadcrumb: [{ label: 'Dashboard' }] },
  },
  {
    path: 'cadastros/pessoas',
    loadComponent: () =>
      import('./features/cadastros/pessoas/pessoas.component').then(m => m.PessoasComponent),
    data: { breadcrumb: [{ label: 'Pessoas' }] },
  },
  {
    path: 'cadastros/pessoas/novo',
    loadComponent: () =>
      import('./features/cadastros/pessoas/pessoa-detalhe/pessoa-detalhe.component').then(
        m => m.PessoaDetalheComponent
      ),
    data: { breadcrumb: [{ label: 'Pessoas', path: 'cadastros/pessoas' }, { label: 'Nova pessoa' }] },
  },
  {
    path: 'cadastros/pessoas/:id',
    loadComponent: () =>
      import('./features/cadastros/pessoas/pessoa-detalhe/pessoa-detalhe.component').then(
        m => m.PessoaDetalheComponent
      ),
    data: { breadcrumb: [{ label: 'Pessoas', path: 'cadastros/pessoas' }, { label: 'Detalhe da pessoa' }] },
  },
  {
    // Enquanto o hub unificado de cadastros (#57) não existe, a URL do hub
    // redireciona para a lista de Pessoas (única já implementada) — ARC-053.
    path: 'cadastros',
    pathMatch: 'full',
    redirectTo: 'cadastros/pessoas',
  },
  {
    path: 'projetos',
    loadComponent: () =>
      import('./features/projetos/projetos.component').then(m => m.ProjetosComponent),
    data: { breadcrumb: [{ label: 'Projetos' }] },
  },
  {
    path: 'projetos/:id/kanban',
    loadComponent: () =>
      import('./features/projetos/kanban/kanban.component').then(m => m.KanbanComponent),
    data: { breadcrumb: [{ label: 'Projetos', path: 'projetos' }, { label: 'Kanban' }] },
  },
  {
    // ADR-013/ARC-068 (front#161): a tela "Clientes" (lista de Pessoas por
    // módulo) foi removida — violava o cadastro de Pessoa único e
    // centralizado. A config por cliente (`ConfiguracaoColetaCliente`)
    // virou a aba condicional "Coleta" no cadastro central de Pessoa
    // (papel Cliente + módulo Coletas). Redireciona por compatibilidade
    // com quem ainda navega para 'coletas' diretamente — não confundir
    // com 'coletas/operacao' abaixo (ARC-060, intocado).
    path: 'coletas',
    pathMatch: 'full',
    redirectTo: 'coletas/certificados',
  },
  {
    path: 'coletas/certificados',
    loadComponent: () =>
      import('./features/coletas/certificados/coletas-certificados.component').then(
        m => m.ColetasCertificadosComponent
      ),
    data: {
      breadcrumb: [
        { label: 'Configurações de coleta', path: 'coletas/certificados' },
        { label: 'Certificados' },
      ],
    },
  },
  {
    path: 'coletas/config',
    loadComponent: () =>
      import('./features/coletas/config/coletas-config.component').then(
        m => m.ColetasConfigComponent
      ),
    data: {
      breadcrumb: [
        { label: 'Configurações de coleta', path: 'coletas/certificados' },
        { label: 'Configurações' },
      ],
    },
  },
  {
    // Módulo novo e isolado (ARC-060) — operação de coletas em campo. Não confundir
    // com 'coletas' acima (retaguarda antiga de configuração por cliente/certificados).
    path: 'coletas/operacao',
    loadComponent: () =>
      import('./features/coletas-operacao/lista/coletas-operacao-lista.component').then(
        m => m.ColetasOperacaoListaComponent
      ),
    data: { breadcrumb: [{ label: 'Coletas' }] },
  },
  {
    // Tela 2/3 do módulo (ARC-060) — Nova coleta.
    path: 'coletas/operacao/nova',
    loadComponent: () =>
      import('./features/coletas-operacao/nova/coletas-operacao-nova.component').then(
        m => m.ColetasOperacaoNovaComponent
      ),
    data: { breadcrumb: [{ label: 'Coletas', path: 'coletas/operacao' }, { label: 'Nova coleta' }] },
  },
  {
    // Tela 4/4 do módulo (ARC-060 C4) — Mapa operacional em tempo real.
    // Precisa vir ANTES de 'coletas/operacao/:id' pelo mesmo motivo de 'nova'.
    path: 'coletas/operacao/mapa',
    loadComponent: () =>
      import('./features/coletas-operacao/mapa/coletas-operacao-mapa.component').then(
        m => m.ColetasOperacaoMapaComponent
      ),
    data: { breadcrumb: [{ label: 'Coletas', path: 'coletas/operacao' }, { label: 'Mapa operacional' }] },
  },
  {
    // Tela 3/4 do módulo (ARC-060) — Detalhe da coleta. Precisa vir DEPOIS de
    // 'coletas/operacao/nova' e 'coletas/operacao/mapa' para que essas rotas
    // não sejam capturadas pelo ':id'.
    path: 'coletas/operacao/:id',
    loadComponent: () =>
      import('./features/coletas-operacao/detalhe/coletas-operacao-detalhe.component').then(
        m => m.ColetasOperacaoDetalheComponent
      ),
    data: { breadcrumb: [{ label: 'Coletas', path: 'coletas/operacao' }, { label: 'Detalhe da coleta' }] },
  },
  {
    path: 'financeiro/contas-pagar',
    loadComponent: () =>
      import('./features/financeiro/contas-pagar/contas-pagar.component').then(
        m => m.ContasPagarComponent
      ),
    data: { breadcrumb: [{ label: 'Financeiro', path: 'financeiro/contas-pagar' }, { label: 'Contas a pagar' }] },
  },
  {
    path: 'financeiro/contas-receber',
    loadComponent: () =>
      import('./features/financeiro/contas-receber/contas-receber.component').then(
        m => m.ContasReceberComponent
      ),
    data: { breadcrumb: [{ label: 'Financeiro', path: 'financeiro/contas-pagar' }, { label: 'Contas a receber' }] },
  },
  {
    // ADR-012: "Cadastros" deixou de ser 1 rota com abas (Contas bancárias/
    // Categoria eram cadastros de entidades distintas, não seções de um
    // registro) — cada uma agora é rota própria. Redireciona por
    // compatibilidade com quem ainda navega para 'financeiro/cadastros'
    // diretamente, e serve de destino intermediário do breadcrumb.
    path: 'financeiro/cadastros',
    pathMatch: 'full',
    redirectTo: 'financeiro/cadastros/contas-bancarias',
  },
  {
    path: 'financeiro/cadastros/contas-bancarias',
    loadComponent: () =>
      import('./features/financeiro/cadastros/contas-bancarias/financeiro-contas-bancarias.component').then(
        m => m.FinanceiroContasBancariasComponent
      ),
    data: {
      breadcrumb: [
        { label: 'Financeiro', path: 'financeiro/contas-pagar' },
        { label: 'Cadastros', path: 'financeiro/cadastros' },
        { label: 'Contas bancárias' },
      ],
    },
  },
  {
    path: 'financeiro/cadastros/categorias',
    loadComponent: () =>
      import('./features/financeiro/cadastros/categorias/financeiro-categorias.component').then(
        m => m.FinanceiroCategoriasComponent
      ),
    data: {
      breadcrumb: [
        { label: 'Financeiro', path: 'financeiro/contas-pagar' },
        { label: 'Cadastros', path: 'financeiro/cadastros' },
        { label: 'Categorias' },
      ],
    },
  },
  {
    path: 'financeiro/parametros',
    loadComponent: () =>
      import('./features/financeiro/parametros/financeiro-parametros.component').then(
        m => m.FinanceiroParametrosComponent
      ),
    data: { breadcrumb: [{ label: 'Financeiro', path: 'financeiro/contas-pagar' }, { label: 'Parâmetros' }] },
  },
  {
    path: 'financeiro/fechamento-agenda',
    loadComponent: () =>
      import('./features/financeiro/fechamento-agenda/fechamento-agenda.component').then(
        m => m.FechamentoAgendaComponent
      ),
    data: { breadcrumb: [{ label: 'Financeiro', path: 'financeiro/contas-pagar' }, { label: 'Fechamento de agenda' }] },
  },
  {
    path: 'financeiro/fechamento-comissao',
    loadComponent: () =>
      import('./features/financeiro/fechamento-comissao/fechamento-comissao.component').then(
        m => m.FechamentoComissaoComponent
      ),
    data: { breadcrumb: [{ label: 'Financeiro', path: 'financeiro/contas-pagar' }, { label: 'Fechamento de comissão' }] },
  },
  {
    path: 'financeiro',
    redirectTo: 'financeiro/contas-pagar',
    pathMatch: 'full',
  },
  {
    path: 'fiscal',
    loadComponent: () =>
      import('./features/fiscal/fiscal.component').then(m => m.FiscalComponent),
    data: { breadcrumb: [{ label: 'Fiscal' }] },
  },
  ...crmRoutes,
  {
    path: 'tickets',
    loadComponent: () =>
      import('./features/tickets/tickets.component').then(m => m.TicketsComponent),
    data: { breadcrumb: [{ label: 'Tickets' }] },
  },
  {
    // ADR-012: "Configurações de SLA" deixou de ser aba (funcionalidade
    // distinta, não seção de um registro) — vira rota própria.
    path: 'tickets/config-sla',
    loadComponent: () =>
      import('./features/tickets/config-sla/tickets-config-sla.component').then(
        m => m.TicketsConfigSlaComponent
      ),
    data: { breadcrumb: [{ label: 'Tickets', path: 'tickets' }, { label: 'Configurações de SLA' }] },
  },
  {
    path: 'servicos',
    loadComponent: () =>
      import('./features/servicos/servicos.component').then(m => m.ServicosComponent),
    data: { breadcrumb: [{ label: 'Ordens de Serviço' }] },
  },
  {
    path: 'certificados',
    loadComponent: () =>
      import('./features/certificados/certificados.component').then(m => m.CertificadosComponent),
    data: { breadcrumb: [{ label: 'Certificados' }] },
  },
  {
    // ADR-012: "Estoque" deixou de ser 1 rota com abas (Depósitos/
    // Configurações/Saldo de produtos eram funcionalidades distintas,
    // não seções de um registro) — cada uma agora é rota própria.
    // Redireciona por compatibilidade com quem ainda navega para
    // 'estoque' diretamente.
    path: 'estoque',
    pathMatch: 'full',
    redirectTo: 'estoque/depositos',
  },
  {
    path: 'estoque/depositos',
    loadComponent: () =>
      import('./features/estoque/depositos/estoque-depositos.component').then(
        m => m.EstoqueDepositosComponent
      ),
    data: { breadcrumb: [{ label: 'Estoque', path: 'estoque/depositos' }, { label: 'Depósitos' }] },
  },
  {
    path: 'estoque/configuracoes',
    loadComponent: () =>
      import('./features/estoque/configuracoes/estoque-configuracoes.component').then(
        m => m.EstoqueConfiguracoesComponent
      ),
    data: {
      breadcrumb: [{ label: 'Estoque', path: 'estoque/depositos' }, { label: 'Configurações' }],
    },
  },
  {
    path: 'estoque/saldo',
    loadComponent: () =>
      import('./features/estoque/saldo/estoque-saldo.component').then(
        m => m.EstoqueSaldoComponent
      ),
    data: {
      breadcrumb: [{ label: 'Estoque', path: 'estoque/depositos' }, { label: 'Saldo de produtos' }],
    },
  },
  {
    path: 'estoque/produto/:id',
    loadComponent: () =>
      import('./features/estoque/produto-ledger/produto-ledger.component').then(
        m => m.ProdutoLedgerComponent
      ),
    data: {
      breadcrumb: [{ label: 'Estoque', path: 'estoque/depositos' }, { label: 'Ledger do produto' }],
    },
  },
  {
    // ADR-012: "Cilindros" deixou de ser 1 rota com abas (Monitor/Configurações
    // eram funcionalidades distintas, não seções de um registro) — cada uma
    // agora é rota própria. Redireciona por compatibilidade com quem ainda
    // navega para 'cilindros' diretamente.
    path: 'cilindros',
    pathMatch: 'full',
    redirectTo: 'cilindros/monitor',
  },
  {
    path: 'cilindros/monitor',
    loadComponent: () =>
      import('./features/cilindros/monitor/cilindros-monitor.component').then(
        m => m.CilindrosMonitorComponent
      ),
    data: { breadcrumb: [{ label: 'Cilindros', path: 'cilindros/monitor' }, { label: 'Monitor' }] },
  },
  {
    // Fase A (ARC-084/front#171) — mapa da carga com posição do veículo e
    // proveniência da geo do cliente (dados mock até o backend, api#386,
    // publicar o canal real — ver cilindros-mapa.models.ts).
    path: 'cilindros/mapa',
    loadComponent: () =>
      import('./features/cilindros/mapa/cilindros-mapa.component').then(m => m.CilindrosMapaComponent),
    data: { breadcrumb: [{ label: 'Cilindros', path: 'cilindros/monitor' }, { label: 'Mapa da carga' }] },
  },
  {
    // Decisão do PO (2026-10-13, front#192): saiu do menu de Cilindros para o
    // grupo Configurações (padrão de 'coletas/config') — breadcrumb segue a
    // nova posição. Rota inalterada, sem quebrar links existentes.
    path: 'cilindros/configuracoes',
    loadComponent: () =>
      import('./features/cilindros/configuracoes/cilindros-configuracoes.component').then(
        m => m.CilindrosConfiguracoesComponent
      ),
    data: {
      breadcrumb: [
        { label: 'Configurações', path: 'configuracoes' },
        { label: 'Configurações de cilindros' },
      ],
    },
  },
  {
    path: 'relatorios',
    loadComponent: () =>
      import('./features/relatorios/relatorios.component').then(m => m.RelatoriosComponent),
    data: { breadcrumb: [{ label: 'Relatórios' }] },
  },
  {
    path: 'configuracoes',
    loadComponent: () =>
      import('./features/configuracoes/configuracoes.component').then(m => m.ConfiguracoesComponent),
    data: { breadcrumb: [{ label: 'Configurações' }] },
  },
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    // ARC-072 (front#169): rota removida/inexistente sob um tenant válido
    // (ex.: 'coletas/clientes', removida no ARC-068) não deve deixar o
    // router-outlet em branco — cai para o dashboard do próprio tenant
    // (redirectTo relativo preserva o :tenantId/whitelabel já resolvido).
    path: '**',
    redirectTo: 'dashboard',
  },
];

export const routes: Routes = [
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/login/login.component').then(m => m.LoginComponent),
      },
      {
        path: 'recuperar-senha',
        loadComponent: () =>
          import('./features/auth/recuperar-senha/recuperar-senha.component').then(
            m => m.RecuperarSenhaComponent
          ),
      },
      {
        path: 'redefinir-senha',
        loadComponent: () =>
          import('./features/auth/redefinir-senha/redefinir-senha.component').then(
            m => m.RedefinirSenhaComponent
          ),
      },
      {
        path: 'cadastro',
        loadComponent: () =>
          import('./features/auth/cadastro/cadastro.component').then(m => m.CadastroComponent),
      },
    ],
  },

  ...adminRoutes,

  {
    path: 'selecionar-tenant',
    loadComponent: () =>
      import('./features/tenant-select/tenant-select.component').then(
        m => m.TenantSelectComponent
      ),
  },

  // ARC-041: página real de acesso negado. Topo da árvore (antes de ':tenantId')
  // para não ser engolida como tenantId; adminGuard e demais guards redirecionam
  // para aqui via UrlTree(['/403']).
  {
    path: '403',
    loadComponent: () =>
      import('./features/forbidden/forbidden.component').then(m => m.ForbiddenComponent),
  },

  // Multitenant: path com :tenantId (app.compleet.com.br)
  {
    path: ':tenantId',
    component: AppShellComponent,
    canActivate: [authGuard, tenantGuard],
    children: tenantFeatureRoutes,
  },

  // Whitelabel: path vazio, tenant resolvido pelo hostname (app.cliente.com.br)
  {
    path: '',
    component: AppShellComponent,
    canActivate: [authGuard, whitelabelGuard],
    children: tenantFeatureRoutes,
  },

  // ARC-072 (front#169): defesa extra — path que não bate com nenhuma rota
  // acima (nem sequer ':tenantId', que já casa qualquer 1º segmento) nunca
  // deve deixar o router-outlet em branco. Na prática o wildcard interno de
  // tenantFeatureRoutes já cobre o caso comum (rota removida sob um tenant
  // válido); este é o último fallback.
  {
    path: '**',
    redirectTo: 'selecionar-tenant',
  },
];
